export interface Company {
  key: string
  title: string
  domain: string
  status: string
  /** `null` para company só com integração de pedidos da plataforma (sem GA4). */
  propertyId: string | null
  integrationDate: string | null
  segment: string
  planType: string
  /** Plataforma da integração de pedidos (`shopify`, `vtex`), ou `null`. */
  orderIntegrationPlatform?: string | null
}

export interface SegmentOption {
  name: string
  count: number
}

export interface PlanOption {
  name: string
  count: number
}

export interface RoiByPlanType {
  planType: string
  companiesCount: number
  avgRoi: number | null
  avgRoiWinsorized: number | null
  totalInfluencedRevenue: number
  totalCost: number
}

/**
 * Conversão POR STORY (byStory do pipeline, modo 'stories').
 * Crédito INTEGRAL: cada story que exibiu o produto comprado recebe o valor
 * cheio do item — a soma entre stories pode exceder o total agregado (que
 * segue sendo o influencedRevenue sem dupla contagem).
 */
export interface StoryConversion {
  storyKey: string
  /** Título real do story (api-admin) — null quando meta indisponível. */
  storyTitle?: string | null
  /** URL da mídia (stream) do vídeo. */
  mediaUrl?: string | null
  /** Preview estático do vídeo. */
  thumbnail?: string | null
  /** Preview animado do vídeo. */
  gif?: string | null
  /** Pedidos influenciados (story_view) creditados ao story. */
  matched: number
  influencedRevenue: number
  /** Pedidos com cart_add feito dentro do story. */
  directMatched: number
  directRevenue: number
  /** Compradores únicos creditados — numerador da taxa. */
  buyersCount: number
  /** Viewers únicos do story no período (todos, não só compradores). */
  viewers: number | null
  /** buyersCount / viewers; null quando viewers indisponível. */
  conversionRate: number | null
  /** Influência por visualização POR VÍDEO (viu este vídeo ≤30d antes de comprar). */
  viewInfluencedMatched?: number
  viewInfluencedRevenue?: number
  /** Itens comprados que bateram com produtos do story. */
  products: string[]
  transactionIds: string[]
}

export interface SegmentProcessResult {
  success: boolean
  segment: string
  companiesTotal: number
  companiesProcessed: number
  companiesFailed: number
  companiesExcluded?: number
  totals: {
    totalOrders: number
    totalRevenue: number
    influencedOrders: number
    influencedRevenue: number
    attributedOrdersRevenue: number
    directOrders: number
  }
  ticketMedio: {
    avgTicket: number
    /** Item-level (base do ROI) — não comparar com avgTicketOrganic. */
    avgTicketInfluenced: number
    /** Pedido inteiro dos atribuídos — mesma unidade de avgTicketOrganic, usar no uplift. */
    avgTicketAttributedOrders: number
    avgTicketOrganic: number
    diffPercent: number | null
  }
  averages: {
    avgOrders: number
    avgRevenue: number
    avgInfluencedOrders: number
    avgInfluencedRevenue: number
    avgConversionRate: number
  }
  averagesFiltered: {
    avgOrders: number
    avgRevenue: number
    avgInfluencedOrders: number
    avgInfluencedRevenue: number
    avgConversionRate: number
    companiesCount: number
  }
  roi: {
    avgRoi: number | null
    avgRoiWinsorized: number | null
    companiesWithRoi: number
    byPlanType: RoiByPlanType[]
  }
  buyerProfile?: {
    withVideo: { newUsers: number; returningUsers: number; returningPercent: number | null; newRevenue: number; returningRevenue: number }
    withoutVideo: { newUsers: number; returningUsers: number; returningPercent: number | null; newRevenue: number; returningRevenue: number }
    diffReturningPercent: number | null
  }
  companies: Array<{
    companyKey: string
    companyTitle: string
    propertyId: string | null
    planType: string
    subscriptionStatus: string
    success: boolean
    error?: string
    totalOrders: number
    totalRevenue: number
    influencedOrders: number
    influencedRevenue: number
    attributedOrdersRevenue: number
    directOrders: number
    avgTicket: number
    avgTicketInfluenced: number
    avgTicketAttributedOrders: number
    avgTicketOrganic: number
    roi: number | null
    roiAdjusted?: number | null
    proportionalCost: number
    lowInvoice?: boolean
    effectiveStartDate?: string
    gaActiveDays?: number
    isFirstMonthPartial?: boolean
    subscriptionCreatedAt?: string
    engagementMetrics?: {
      withVideo: { sessions: number; totalUsers: number; bounceRate: number; avgSessionDuration: number; pagesPerSession: number }
      withoutVideo: { sessions: number; totalUsers: number; bounceRate: number; avgSessionDuration: number; pagesPerSession: number }
    }
  }>
  autoExcludeKeys?: string[]
  autoExcludeByCheck?: Record<string, number>
  /**
   * Partição das empresas fora do cálculo — uma entrada por empresa, com um só
   * motivo. Espelha `company-disposition.ts` do backend. Opcional porque
   * resultado vindo de cache antigo não tem o campo.
   */
  exclusions?: CompanyExclusion[]
}

export type ExclusionReason =
  | 'low_invoice'
  | 'failed'
  | 'no_orders'
  | 'no_cost'
  | 'health_check'

export interface CompanyExclusion {
  companyKey: string
  companyTitle?: string
  reason: ExclusionReason
  checks?: string[]
  error?: string
}

export interface Order {
  transactionId: string
  date: string
  widdeUid: string
  purchaseRevenue: number
  items: OrderItem[]
  processed: boolean
  widdeInfluenciou: boolean
  widdeDireto: boolean
  influencedItemRevenue?: number
  directItemRevenue?: number
  /** Influência por visualização: viu vídeo e comprou depois (sem match de produto). */
  viewInfluenced?: boolean
}

export interface OrderItem {
  itemId: string
  itemName: string
  itemsPurchased: number
  itemRevenue: number
}

export interface TimelineEvent {
  event_name: string
  event_at: string
  session_key?: string
  story_key?: string
  collection_key?: string
  media_key?: string
  product_key?: string
  properties?: string | Record<string, any>
}

/** Estado de um processamento rastreado no backend (`process-tracker`); `lost` = o servidor não o conhece mais. */
export type TrackedProcessStatus = 'running' | 'completed' | 'failed' | 'cancelled' | 'lost'

/** Uma linha da janela de debug: etapa medida, progresso ou anotação. */
export interface ProcessEvent {
  seq: number
  at: string
  kind: 'progress' | 'step-start' | 'step-end' | 'step-error' | 'log'
  step?: string
  label: string
  durationMs?: number
  error?: string
  data?: Record<string, string | number | boolean | null>
}

export interface ProcessSnapshot {
  sessionId: string
  label: string
  status: Exclude<TrackedProcessStatus, 'lost'>
  startedAt: string
  finishedAt: string | null
  error: string | null
  events: ProcessEvent[]
  lastProgress: ProcessProgress | null
}

export interface ProcessProgress {
  /** Sessão que emitiu (ausente em backend antigo e no estado local de cancelamento). */
  sessionId?: string
  step: 'download' | 'timeline' | 'influence' | 'complete' | 'error'
  message: string
  timestamp: string
  data?: SegmentProcessResult | {
    orders?: number
    totalRevenue?: number
    matched?: number
    widdeInfluenced?: number
    widdeRevenue?: number
    conversionRate?: number
  }
}

export interface DatePreset {
  label: string
  days: number
}

/**
 * Janelas de atribuição pra Live Commerce. Espelha o `LIVE_ATTRIBUTION_WINDOWS`
 * do api-admin (`domain/attribution/attribution-window.const.ts`). Mantenha
 * sincronizado caso novas janelas sejam adicionadas no backend.
 */
export type LiveAttributionWindow = '4h' | '6h' | '24h' | '3d' | '5d' | '7d'

export const LIVE_ATTRIBUTION_WINDOW_LABELS: Record<LiveAttributionWindow, string> = {
  '4h': '4 horas',
  '6h': '6 horas',
  '24h': '24 horas',
  '3d': '3 dias',
  '5d': '5 dias',
  '7d': '7 dias',
}

export const LIVE_ATTRIBUTION_WINDOW_ORDER: LiveAttributionWindow[] = ['4h', '6h', '24h', '3d', '5d', '7d']

/**
 * Detalhamento por live: cada live é um "produto" separado pra fins de
 * report. Soma das stats por live pode ser maior que a stat agregada
 * (porque um pedido pode dar match em múltiplas lives — ex.: produto X
 * apareceu em 2 lives diferentes que o usuário viu).
 */
export interface LiveAttributionProduct {
  key: string
  productId: string
  title: string
}

export interface LiveAudienceTransaction {
  transactionId: string
  widdeUid: string
  /** YYYYMMDDHHMM */
  dateHourMinute: string
  items: { itemName: string; itemRevenue: number }[]
  matched: boolean
  matchedItemNames: string[]
  /** Casou por CLIQUE na overlay (live-direct). Ausente em payloads antigos. */
  directMatched?: boolean
  directMatchedItemNames?: string[]
  /** Preenchido quando o viewer se identificou durante a live (live_lead_captured). */
  leadName?: string
  leadContact?: string
}

// ─── Relatório de audiência da live (espelho do api-admin) ───────────────────

export interface LiveAudienceReportTrafficRow {
  source: string
  medium: string
  campaign: string
  content: string
  viewers: number
  pct: number
}

export interface LiveAudienceReportWatchBand {
  label: string
  minSec: number
  maxSec: number | null
  viewers: number
  pct: number
}

export interface LiveAudienceReportProductClicks {
  productId: string
  title: string
  clicks: number
  clickers: number
  /** Primeiro clique (ISO, UTC) — quando o produto apareceu. Opcional: api-admin anterior não manda. */
  firstClickAt?: string | null
}

/** Espectador mais engajado, sem identificação. */
export interface LiveAudienceReportTopViewer {
  rank: number
  watchSec: number
  messages: number
  clicks: number
}

export interface LiveAudienceReportFunnel {
  windowStart: string
  windowEnd: string
  clicks: number
  clickers: number
  cartAdds: number
  cartAdders: number
  checkoutStarters: number
}

/** Espelho de `live-audience-report.types.ts` do backend. */
export interface LiveAudienceReport {
  live: {
    liveKey: string
    title: string
    status: string
    liveStartedAt: string | null
    endedAt: string | null
    durationSec: number | null
  }
  audience: {
    uniqueViewers: number
    views: number
    peakConcurrent: { value: number | null; source: 'approx' | 'mux-realtime' | 'live-summary' | null }
    watchTime: { totalSec: number; avgSec: number; maxSec: number; sessions: number }
    watchTimeBands: LiveAudienceReportWatchBand[]
    byDevice: Array<{ device: string; viewers: number; views: number; pct: number }>
    traffic: LiveAudienceReportTrafficRow[]
    /** Top 5 por tempo assistido. Opcional: api-admin anterior não manda. */
    topViewers?: LiveAudienceReportTopViewer[]
  }
  engagement: {
    leads: number
    viewerToLeadPct: number | null
    termsAccepted: number
    chat: {
      messages: number
      participants: number
      msgsPerParticipant: number | null
      byDevice: Array<{ device: string; msgsPerUser: number }>
    }
    curve: Array<{ minute: number; viewers: number }>
    /** Likes/compartilhamentos do resumo pós-live; `null` = sem resumo, ausente = api-admin anterior. */
    reactions?: { likes: number; shares: number } | null
  }
  /** Carrinho atribuível à live (resumo pós-live); `null` = sem resumo. */
  liveCart?: { items: number; valueCents: number; users: number } | null
  replay: { sessions: number; watchTimeSec: number }
  products: {
    total: number
    spotlight: number
    clicks: number
    clickers: number
    clicksByProduct: LiveAudienceReportProductClicks[]
  }
  funnel: LiveAudienceReportFunnel
}

// ─── Camadas de receita (espelho de live-revenue-layers.ts) ──────────────────

export type LiveRevenueLayerId = 'full-funnel' | 'click-direct' | 'influenced'

export interface LiveRevenueLayerProduct {
  name: string
  liveProductKey: string | null
  productId: string | null
  clicks: number | null
  clickers: number | null
  cartAdders: number | null
  unitsSold: number
  revenue: number
  orders: number
}

export interface LiveRevenueLayer {
  id: LiveRevenueLayerId
  products: LiveRevenueLayerProduct[]
  unitsSold: number
  revenue: number
  orders: number
  /** % da receita total das três camadas (inteiro; as três fecham em 100). */
  sharePct: number
}

/**
 * As três camadas de receita da live: funil completo (clique + carrinho +
 * compra), venda direta (clique + compra) e influenciados (mesmo pedido, sem
 * clique). A soma é a "receita atribuída" do relatório entregue ao cliente.
 */
export interface LiveRevenueLayers {
  layers: Record<LiveRevenueLayerId, LiveRevenueLayer>
  totalUnits: number
  totalRevenue: number
  orders: number
  sources: { clicks: boolean; cart: boolean }
}

/** Contexto da adição, quando conhecido. Espelho de `live-cart-audience.ts`. */
export type LiveCartItemSource = 'this-live' | 'other-live' | 'video' | 'unknown'

/** Qual pipeline enxergou o item: nossos eventos, o GA4, ou os dois. */
export type LiveCartItemOrigin = 'widde' | 'ga4' | 'both'

/**
 * Quando o item entrou no carrinho, em relação à live. `unconfirmed`: o GA4 só
 * deu o dia/período e ele cruza a live — fica fora do "carrinho da live".
 */
export type LiveCartItemPhase = 'before-live' | 'during-live' | 'after-live' | 'unconfirmed'

/** Precisão do instante da adição — o GA4 nem sempre entrega minuto. */
export type LiveCartTimePrecision = 'minute' | 'hour' | 'day' | 'range'

/** `exact`, `inferred` (hora do GA4 + nosso live_view) ou `unconfirmed`. */
export type LiveCartPhaseConfidence = 'exact' | 'inferred' | 'unconfirmed'

/** De onde saiu o preço unitário. `none` = desconhecido, o valor não é somado. */
export type LiveCartPriceSource = 'widde-event' | 'ga4-observed' | 'none'

export interface LiveCartPurchase {
  transactionId: string
  /** YYYYMMDDHHMM. Vazio quando a compra veio do agregado do GA4. */
  dateHourMinute: string
  itemName: string
  itemRevenue: number
}

/** Um item no carrinho de um espectador. */
export interface LiveCartItem {
  productId: string
  productTitle: string
  unitPrice: number
  priceSource: LiveCartPriceSource
  addedQuantity: number
  removedQuantity: number
  purchasedQuantity: number
  /** Unidades que sobraram no carrinho. */
  quantity: number
  /** `unitPrice × addedQuantity` — o que entrou no carrinho (backend novo). */
  addedValue?: number
  /** `unitPrice × quantity`; zero quando o preço é desconhecido. */
  total: number
  origin: LiveCartItemOrigin
  firstAddedAt: string
  lastAddedAt: string
  timePrecision: LiveCartTimePrecision
  source: LiveCartItemSource
  phase: LiveCartItemPhase
  phaseConfidence?: LiveCartPhaseConfidence
  addedBeforeLive: boolean
  /** Dias parado no carrinho até a live; `null` quando não se aplica. */
  daysInCartBeforeLive: number | null
  inLiveCatalog: boolean
  liveProductKey: string | null
  /** Compra do MESMO item dentro da janela de atribuição. */
  purchase: LiveCartPurchase | null
  purchasedBeforeLive: boolean
  /** Já estava parado no carrinho quando a live começou. */
  pendingBeforeLive: boolean
  /** `pendingBeforeLive && purchase` — a live converteu um item parado. */
  recovered: boolean
  /** Sobrou no carrinho: sem compra na janela e com unidade restante. */
  stillInCart: boolean
  /** `stillInCart` E a janela já fechou — é o abandono que vai para o cliente. */
  abandoned: boolean
}

export interface LiveCartViewer {
  widdeUid: string
  firstViewAt: string
  views: number
  identified: boolean
  leadName: string | null
  leadContact: string | null
  items: LiveCartItem[]
  cartTotal: number
  purchasedFromCartRevenue: number
  /** Σ `total` dos itens abandonados. */
  abandonedValue: number
  ordersInWindow: number
  revenueInWindow: number
}

export interface LiveCartBucket {
  items: number
  value: number
  viewers: number
}

export interface LiveCartGa4Status {
  available: boolean
  precision: LiveCartTimePrecision | null
  /** O GA4 agrupou parte das linhas em `(other)` — lista incompleta. */
  truncatedByCardinality: boolean
  rowsForLive: number
  /** Espectadores que compraram na janela / desses, com add_to_cart no GA4. */
  purchasersInWindow?: number
  purchasersWithAddToCart?: number
}

export interface LiveCartValueBucket {
  items: number
  value: number
}

/**
 * Carrinho da live: o que os espectadores ADICIONARAM durante a live em
 * qualquer carrinho (Widde + nativo da loja via GA4), comprado ou não.
 * Espelho de `LiveCartTotals` (live-cart-audience.ts).
 */
export interface LiveCartTotals {
  items: number
  value: number
  viewers: number
  valueIsPartial: boolean
  byOrigin: Record<LiveCartItemOrigin, LiveCartValueBucket>
  purchased: LiveCartValueBucket
  abandoned: LiveCartValueBucket
  pending: LiveCartValueBucket
  inferredItems: number
  /** Horário não confirmado — fora dos totais. */
  unconfirmed: LiveCartValueBucket
}

/** Métrica oficial de live: pedidos + valor dos ITENS que entram nela. */
export interface LiveMetricValue {
  orders: number
  revenue: number
}

/**
 * Métricas OFICIAIS de live (ver `live-metrics.ts`):
 * - atribuída: espectadores compraram produto do catálogo (padrão);
 * - atribuída direta: além disso, adicionaram ao carrinho Widde pela live
 *   (`null` = não apurada, não zero);
 * - influenciada: compras dos espectadores FORA do catálogo.
 */
export interface LiveOfficialMetrics {
  attributed: LiveMetricValue
  attributedDirect: LiveMetricValue | null
  influenced: LiveMetricValue
}

export type LiveCaveatSeverity = 'info' | 'warning' | 'critical'
export type LiveMetricKey = 'attributed' | 'attributedDirect' | 'influenced' | 'liveCart' | 'abandonedCart'

/** O que um número de live NÃO garante nesta execução (ver `live-caveats.ts`). */
export interface LiveCaveat {
  code: string
  severity: LiveCaveatSeverity
  message: string
  affects: LiveMetricKey[]
  liveKeys?: string[]
}

export interface LiveCartAudienceSummary {
  viewers: number
  viewersListed: number
  viewersTruncated: boolean
  identifiedViewers: number
  viewersWithCart: number
  cartItems: number
  cartValue: number
  /** Algum item sem preço conhecido — `cartValue` sub-reporta. */
  cartValueIsPartial: boolean
  itemsInLiveCatalog: number
  /** Já estavam parados no carrinho quando a live começou. */
  pendingBeforeLive: LiveCartBucket
  itemsPurchasedInWindow: number
  revenuePurchasedInWindow: number
  recovered: { items: number; revenue: number; viewers: number }
  recoveredInLiveCatalog: { items: number; revenue: number; viewers: number }
  /** Abandonados: janela fechada e sem compra. */
  abandoned: LiveCartBucket
  abandonedInLiveCatalog: LiveCartBucket
  /** Sobraram no carrinho com a janela AINDA ABERTA — veredito pendente. */
  pending: LiveCartBucket
  itemsByPhase: Partial<Record<LiveCartItemPhase, number>>
  itemsByOrigin: Record<LiveCartItemOrigin, number>
  /** Carrinho da live (backend novo). */
  liveCart?: LiveCartTotals
  windowClosed: boolean
  /** ISO do fim da janela — o instante em que o abandono é declarado. */
  windowEndsAt: string
  ga4: LiveCartGa4Status
  lookbackDays: number
}

/**
 * Público da live pessoa a pessoa com o carrinho de cada uma. O carrinho vem do
 * GA4 (`add_to_cart` da loja inteira) fundido com os nossos eventos de carrinho
 * (telas Widde, que trazem instante, preço e contexto). Espelho de
 * `live-cart-audience.ts`.
 */
export interface LiveCartAudience {
  summary: LiveCartAudienceSummary
  viewers: LiveCartViewer[]
}

export interface LiveAttributionByLive {
  liveKey: string
  liveTitle: string
  liveStartedAt: string
  liveEndedAt: string | null
  /** Limite final RAW da janela (= liveEndedAt + windowMs). */
  attributionWindowEnd: string
  /** Limite EFETIVO da janela considerando "próxima live" — `min(attributionWindowEnd,
   *  próximaLiveStart)`. Quando há uma live consecutiva, esse valor é menor que o raw
   *  pra evitar overlap de atribuição. */
  effectiveAttributionWindowEnd: string
  /** Receita ATRIBUÍDA desta live (nome legado). */
  liveInfluencedMatched: number
  liveInfluencedRevenue: number
  /** Clique na overlay → compra. Uso interno. */
  liveDirectMatched: number
  liveDirectRevenue: number
  /** Métricas oficiais desta live (backend novo). */
  metrics?: LiveOfficialMetrics
  /** Ressalvas desta live (backend novo). Vazio = nenhuma. */
  caveats?: LiveCaveat[]
  /** Catálogo da live (produtos cadastrados). Pra `live-direct` só
   *  contém os produtos efetivamente clicados. */
  products: LiveAttributionProduct[]
  /** Pedidos do público da live (in-window). Inclui matched + non-matched
   *  pra validação manual via UI. */
  audienceTransactions: LiveAudienceTransaction[]
  /** Vendas da loja na janela da live vs baseline de dias sem live —
   *  presente quando o pipeline teve pedidos + período pra calcular. */
  salesComparison?: LiveSalesComparison
  /** Compradores da janela separados em assistiu/não-assistiu, com a origem
   *  (UTM/referrer) de cada grupo. */
  audienceOrigin?: LiveAudienceOrigin
  /** Taxa de conversão por grupo. Ausente quando o alcance (denominador) não
   *  pôde ser lido do ClickHouse. */
  conversion?: LiveConversionRates
  /** As três taxas no recorte do DIA da live — inclui o grupo orgânico, que
   *  só tem denominador nessa granularidade. */
  dailyConversion?: DailyConversionRates
  /** Origem do tráfego orgânico no dia, vinda do GA4 (em SESSÕES). */
  organicOrigin?: OrganicTrafficOrigin
  /** Espectadores com o carrinho de cada um, cruzado com catálogo e compras.
   *  Ausente quando a leitura falhou ou o pipeline rodou sem audiência. */
  cartAudience?: LiveCartAudience
  /** Relatório de audiência/engajamento (espectadores, tempo assistido, chat,
   *  leads, origem, funil). Base do relatório da live (HTML/PDF). */
  audienceReport?: LiveAudienceReport
  /** As três camadas de receita. Base da receita, pedidos e vendidos do relatório da live. */
  revenueLayers?: LiveRevenueLayers
}

/** Uma origem de tráfego do GA4, medida em SESSÕES (não pessoas). */
export interface TrafficOriginRow {
  source: string
  medium: string
  campaign: string
  sessions: number
  transactions: number
  revenue: number
}

/**
 * Origem do tráfego orgânico no dia. `scope: 'all-store'` significa que o GA4
 * não aceitou isolar quem não tem `widde_uid` e as linhas são da loja inteira
 * — contexto útil, mas não é "o orgânico".
 */
export interface OrganicTrafficOrigin {
  date: string
  scope: 'organic' | 'all-store'
  rows: TrafficOriginRow[]
}

/** De onde saiu o número de pessoas compradoras do grupo orgânico. */
export type OrganicBuyersSource = 'ga4' | 'estimated'

/**
 * As três taxas no recorte do DIA — comparáveis entre si. A do grupo orgânico
 * cruza `totalUsers` do GA4 com contagem de cookies nossa (réguas de "pessoa"
 * diferentes, sem chave em comum), então é aproximada. Espelho do backend
 * `live-conversion.ts`.
 */
export interface DailyConversionRates {
  /** `YYYYMMDD` do dia da live, no fuso da property. */
  date: string
  liveViewers: LiveConversionGroup
  widdeOutsideLive: LiveConversionGroup
  organic: LiveConversionGroup
  organicBuyersSource: OrganicBuyersSource
  /** Alcance qualificado (ClickHouse) excedeu `totalUsers` (GA4). */
  reachInconsistent: boolean
}

export interface LiveConversionGroup {
  /** Pessoas do grupo que compraram na janela. */
  buyers: number
  /** Pessoas do grupo alcançadas na janela (denominador). */
  reach: number
  /** buyers ÷ reach, em %. `null` sem denominador — nunca 0%. */
  ratePct: number | null
}

/**
 * Taxas por grupo. O grupo orgânico NÃO tem taxa: faltam os dois lados
 * (pessoas por trás dos pedidos sem uid, e visitantes que nunca passaram por
 * uma aplicação Widde). Espelho do backend `live-conversion.ts`.
 */
export interface LiveConversionRates {
  liveViewers: LiveConversionGroup
  widdeOutsideLive: LiveConversionGroup
  /** Compradores identificados por contato ANTERIOR à janela — fora das taxas. */
  buyersWithPriorContactOnly: number
}

/** Uma origem de tráfego dentro de um grupo de compradores. */
export interface LiveOriginRow {
  /** `utm_source`, `referral:<domínio>` ou `direto`. */
  source: string
  medium: string
  campaign: string
  content: string
  /** Pessoas distintas com essa origem. */
  buyers: number
  orders: number
  revenue: number
}

export interface LiveAudienceGroup {
  /** Pessoas distintas (uniq widde_uid). */
  buyers: number
  orders: number
  revenue: number
  /** Origens do grupo, maior primeiro. Σ `buyers` = `buyers` do grupo. */
  byOrigin: LiveOriginRow[]
}

/**
 * Compradores da janela de atribuição separados por presença na live.
 * Cobre apenas compradores com `widde_uid` no GA4 — quem não tem uid não
 * aparece em nenhum dos dois grupos. Não contém taxa de conversão: o
 * denominador de visitantes exigiria `session_start` (só plano PRO).
 * Espelho do backend `live-audience-origin.ts`.
 */
export interface LiveAudienceOrigin {
  /** Compraram na janela E têm evento de presença desta live. */
  viewers: LiveAudienceGroup
  /** Compraram na janela SEM evento de presença desta live. */
  nonViewers: LiveAudienceGroup
}

export interface LiveSalesBucket {
  orders: number
  revenue: number
}

/**
 * Comparação de vendas por live (incrementalidade aproximada):
 * total da loja na janela de atribuição, separado entre público da live e
 * quem não assistiu, mais baseline (média da mesma faixa de horário/duração
 * em dias sem live). Espelho do backend `live-sales-comparison.ts`.
 */
/** Um dia sem live no baseline, na mesma faixa de horário da live. */
export interface LiveBaselineSample {
  /** Início da faixa deslocada (epoch UTC) — exibir no fuso da property. */
  startMs: number
  orders: number
  revenue: number
}

/**
 * Vendas da loja na janela da live, divididas em TRÊS grupos que fecham com o
 * total — e não dois. O antigo "não assistiram" carregava todo pedido sem
 * `widde_uid` (a maioria), afirmando o que os dados não sustentam.
 */
export interface LiveSalesComparison {
  /** Total = viewers + identifiedNonViewers + unidentified. */
  window: LiveSalesBucket
  /** Identificados COM evento de presença desta live na janela. */
  viewers: LiveSalesBucket
  /** Identificados SEM evento de presença — sabemos que não assistiram. */
  identifiedNonViewers: LiveSalesBucket
  /** Sem `widde_uid` — não há como saber se assistiram. */
  unidentified: LiveSalesBucket
  /** Dia civil em que a live começou, medido com os MESMOS pedidos (contexto). */
  day: LiveSalesBucket
  /** Dias civis que a janela toca (backend novo). Janela de 24h costuma pegar 2. */
  windowDays?: LiveSalesBucket & { count: number; firstDay: string; lastDay: string }
  /** Receita da janela ÷ do dia da live, em %. Passa de 100 quando a janela atravessa a meia-noite. */
  windowShareOfDayPct: number | null
  /** Receita da janela ÷ dos dias que ela toca, em % (≤ 100). Backend novo. */
  windowShareOfWindowDaysPct?: number | null
  baseline: {
    sampleDays: number
    avgOrders: number | null
    avgRevenue: number | null
    /** Dias que compõem a média, cronológico — torna a comparação auditável. */
    samples: LiveBaselineSample[]
    medianRevenue: number | null
    minRevenue: number | null
    maxRevenue: number | null
    /** Dias sem live que venderam MENOS que a janela da live. */
    daysBelowLive: number | null
  }
  upliftOrdersPct: number | null
  upliftRevenuePct: number | null
}

/**
 * Stats agregadas de attribution da Live retornadas pelo pipeline.
 * Sempre por empresa (escopo de uma execução). Stories tem suas próprias
 * stats em `OrderStats` — esses dois mundos vivem em paralelo.
 */
export interface LiveAttributionStats {
  /** Janela usada na execução que gerou esses números. */
  window: LiveAttributionWindow
  /** Pedidos com receita ATRIBUÍDA (item do catálogo da live). Nome legado. */
  liveMatched: number
  /** Receita ATRIBUÍDA (itens do catálogo). Nome legado: "influenced". */
  liveInfluencedRevenue: number
  /** Clique na overlay → compra. Uso interno, não é métrica de comunicação. */
  liveDirectMatched: number
  liveDirectRevenue: number
  /** Métricas oficiais (backend novo). */
  liveMetrics?: LiveOfficialMetrics
  /** Ressalvas do período (backend novo). Vazio = nenhuma. */
  liveCaveats?: LiveCaveat[]
  /** Detalhamento por live (cada live é um item independente). */
  byLive: LiveAttributionByLive[]
}

/** Modo do pipeline. Stories e Live são processados isoladamente para não
 *  sobrecarregar nenhum dos dois — são "produtos diferentes" com janelas
 *  e necessidades distintas. */
export type AttributionMode = 'stories' | 'live'

export interface ProcessOptions {
  companyKey?: string
  segment?: string
  planType?: string
  startDate?: string
  endDate?: string
  forceDownload?: boolean
  forceProcess?: boolean
  filterField?: 'segment' | 'planType'
  /** Janela de atribuição usada APENAS pro cálculo de live commerce.
   *  Stories continua com sua janela própria (30d, definida pela audience GA4). */
  liveAttributionWindow?: LiveAttributionWindow
  /** Define qual lado do pipeline rodar. Default 'stories' (compat). */
  mode?: AttributionMode
}

// Data Quality Analysis types
export interface MetricStats {
  mean: number
  median: number
  stdDev: number
  q1: number
  q3: number
  iqr: number
  lowerFence: number
  upperFence: number
  min: number
  max: number
}

export type AnomalyType =
  | 'roi_outlier_high'
  | 'roi_outlier_low'
  | 'cost_disproportionate'
  | 'partial_ga'
  | 'zero_orders'
  | 'zero_cost'
  | 'first_month_partial'
  | 'high_discount_suspect'
  | 'no_active_videos'
  | 'influenced_exceeds_total'
  | 'high_influence_rate'
  | 'canceled_during_period'
  | 'no_video_sessions_with_influence'
  | 'revenue_outlier'
  | 'ticket_medio_anomaly'
  | 'zero_influenced'

export type AnomalySeverity = 'critical' | 'warning' | 'info'

export interface DataQualityAnomaly {
  companyKey: string
  companyTitle: string
  planType: string
  type: AnomalyType
  severity: AnomalySeverity
  metric: string
  value: number | null
  expected: { min: number; max: number } | null
  description: string
  details: Record<string, any>
}

export interface CompanyQualityData {
  companyKey: string
  companyTitle: string
  planType: string
  subscriptionStatus: string
  totalOrders: number
  totalRevenue: number
  influencedOrders: number
  influencedRevenue: number
  directOrders: number
  roi: number | null
  roiAdjusted: number | null
  proportionalCost: number
  gaActiveDays: number | undefined
  isFirstMonthPartial: boolean
  subscriptionCreatedAt: string | undefined
  effectiveStartDate: string | undefined
  conversionRate: number
  influencedPercent: number
  anomalyFlags: string[]
  // Engagement metrics segmented by Widde audience
  sessionsWithVideo: number
  sessionsWithoutVideo: number
  usersWithVideo: number
  usersWithoutVideo: number
  bounceRateWithVideo: number
  bounceRateWithoutVideo: number
  avgSessionDurationWithVideo: number
  avgSessionDurationWithoutVideo: number
  pagesPerSessionWithVideo: number
  pagesPerSessionWithoutVideo: number
  activeVideos: number
}

export interface DataQualityReport {
  anomalies: DataQualityAnomaly[]
  statistics: {
    roi: MetricStats
    revenue: MetricStats
    influencedPercent: MetricStats
    cost: MetricStats
    conversionRate: MetricStats
  }
  companies: CompanyQualityData[]
  summary: {
    totalCompanies: number
    companiesWithAnomalies: number
    criticalCount: number
    warningCount: number
    infoCount: number
  }
}
